import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../services/api';

function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profile, setProfile] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    organizationName: '',
    createdAt: ''
  });
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data } = await auth.getProfile();
      setProfile({
        email: data.email || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        role: data.role || '',
        organizationName: data.organizationName || '',
        createdAt: data.createdAt || ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error carregant el perfil' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const { data } = await auth.updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName
      });
      if (updateUser) {
        updateUser(data.user);
      }
      setMessage({ type: 'success', text: 'Perfil actualitzat correctament' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error actualitzant el perfil' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({ type: 'error', text: 'Les contrasenyes no coincideixen' });
      return;
    }

    if (passwords.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'La nova contrasenya ha de tenir almenys 8 caràcters' });
      return;
    }

    try {
      setChangingPassword(true);
      setMessage({ type: '', text: '' });
      await auth.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'Contrasenya canviada correctament' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error canviant la contrasenya' });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-content">El meu perfil</h1>

      {message.text && (
        <div className={`p-4 rounded-lg ${message.type === 'error' ? 'bg-error-subtle text-error-text border border-error/30' : 'bg-success-subtle text-success-text border border-success/30'}`}>
          {message.text}
        </div>
      )}

      {/* Profile Information */}
      <div className="card">
        <h2 className="text-lg font-semibold text-content mb-4">Informació personal</h2>
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Correu electrònic
            </label>
            <input
              type="email"
              value={profile.email}
              disabled
              className="input w-full bg-surface-muted cursor-not-allowed"
            />
            <p className="text-xs text-content-muted mt-1">El correu electrònic no es pot canviar</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Nom
              </label>
              <input
                type="text"
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                className="input w-full"
                placeholder="El teu nom"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Cognoms
              </label>
              <input
                type="text"
                value={profile.lastName}
                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                className="input w-full"
                placeholder="Els teus cognoms"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Rol
              </label>
              <input
                type="text"
                value={profile.role}
                disabled
                className="input w-full bg-surface-muted cursor-not-allowed capitalize"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Organització
              </label>
              <input
                type="text"
                value={profile.organizationName || '-'}
                disabled
                className="input w-full bg-surface-muted cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Membre des de
            </label>
            <input
              type="text"
              value={profile.createdAt ? new Date(profile.createdAt).toLocaleDateString('ca-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
              disabled
              className="input w-full bg-surface-muted cursor-not-allowed"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary disabled:opacity-50"
            >
              {saving ? 'Guardant...' : 'Guardar canvis'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="card">
        <h2 className="text-lg font-semibold text-content mb-4">Canviar contrasenya</h2>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Contrasenya actual
            </label>
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Nova contrasenya
            </label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              className="input w-full"
              minLength={8}
              required
            />
            <p className="text-xs text-content-muted mt-1">Mínim 8 caràcters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Confirmar nova contrasenya
            </label>
            <input
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
              className="input w-full"
              required
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={changingPassword}
              className="btn btn-secondary disabled:opacity-50"
            >
              {changingPassword ? 'Canviant...' : 'Canviar contrasenya'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfilePage;
